import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Avatar,
  Divider,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import {
  getPendingPhysicians,
  getPendingSponsors,
  approvePhysician,
  rejectPhysician,
  approveSponsor,
  rejectSponsor
} from '../../services/adminService';

// Icons
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 1.5 }}>{children}</Box>}
    </div>
  );
};

const PendingApprovalsTab = ({ onApprovalProcessed }) => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [physicians, setPhysicians] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: '', // 'approve' or 'reject'
    userType: '', // 'physician' or 'sponsor'
    user: null
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Load pending applications
  useEffect(() => {
    loadPendingApplications();
  }, []);

  const loadPendingApplications = async () => {
    setLoading(true);
    try {
      const [physiciansResult, sponsorsResult] = await Promise.all([
        getPendingPhysicians(),
        getPendingSponsors()
      ]);

      if (physiciansResult.data) {
        setPhysicians(physiciansResult.data);
      }
      if (sponsorsResult.data) {
        setSponsors(sponsorsResult.data);
      }
    } catch (error) {
      console.error('Error loading pending applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const openActionDialog = (type, userType, userData) => {
    setActionDialog({
      open: true,
      type,
      userType,
      user: userData
    });
    setRejectionReason('');
  };

  const closeActionDialog = () => {
    setActionDialog({
      open: false,
      type: '',
      userType: '',
      user: null
    });
    setRejectionReason('');
  };

  const handleApprove = async () => {
    if (!actionDialog.user || !user) return;

    setProcessing(true);
    try {
      let result;
      if (actionDialog.userType === 'physician') {
        const physicianData = actionDialog.user.physicians?.[0];
        result = await approvePhysician(
          actionDialog.user.id,
          physicianData?.id
        );
      } else {
        const sponsorData = actionDialog.user.sponsors?.[0];
        result = await approveSponsor(
          actionDialog.user.id,
          sponsorData?.id
        );
      }

      if (result.success) {
        // Reload the lists
        await loadPendingApplications();
        closeActionDialog();
        // Notify parent to refresh counts
        if (onApprovalProcessed) {
          onApprovalProcessed();
        }
      }
    } catch (error) {
      console.error('Error approving user:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!actionDialog.user || !user || !rejectionReason.trim()) return;

    setProcessing(true);
    try {
      let result;
      if (actionDialog.userType === 'physician') {
        const physicianData = actionDialog.user.physicians?.[0];
        result = await rejectPhysician(
          actionDialog.user.id,
          physicianData?.id,
          user.id,
          rejectionReason
        );
      } else {
        const sponsorData = actionDialog.user.sponsors?.[0];
        result = await rejectSponsor(
          actionDialog.user.id,
          sponsorData?.id,
          user.id,
          rejectionReason
        );
      }

      if (result.success) {
        // Reload the lists
        await loadPendingApplications();
        closeActionDialog();
        // Notify parent to refresh counts
        if (onApprovalProcessed) {
          onApprovalProcessed();
        }
      }
    } catch (error) {
      console.error('Error rejecting user:', error);
    } finally {
      setProcessing(false);
    }
  };

  const PhysicianCard = ({ physician }) => {
    const physicianData = physician.physicians?.[0] || {};
    const createdDate = new Date(physician.created_at).toLocaleDateString();

    return (
      <Card 
        elevation={2} 
        sx={{ 
          height: '100%',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: 'primary.main', 
                width: 56, 
                height: 56, 
                mr: 2 
              }}
            >
              <LocalHospitalIcon fontSize="large" />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {physician.first_name} {physician.last_name}
              </Typography>
              <Chip 
                label="Physician" 
                color="primary" 
                size="small" 
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                {physician.email}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BadgeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2">
                <strong>License:</strong> {physicianData.medical_license_number || 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WorkIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2">
                <strong>Specialty:</strong> {physicianData.specialty || 'N/A'}
              </Typography>
            </Box>

            {physicianData.years_of_experience && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2">
                  <strong>Experience:</strong> {physicianData.years_of_experience} years
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Applied: {createdDate}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => openActionDialog('approve', 'physician', physician)}
            fullWidth
            sx={{ mr: 1 }}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => openActionDialog('reject', 'physician', physician)}
            fullWidth
          >
            Reject
          </Button>
        </CardActions>
      </Card>
    );
  };

  const SponsorCard = ({ sponsor }) => {
    const sponsorData = sponsor.sponsors?.[0] || {};
    const createdDate = new Date(sponsor.created_at).toLocaleDateString();

    return (
      <Card 
        elevation={2} 
        sx={{ 
          height: '100%',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: 'secondary.main', 
                width: 56, 
                height: 56, 
                mr: 2 
              }}
            >
              <VolunteerActivismIcon fontSize="large" />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {sponsor.first_name} {sponsor.last_name}
              </Typography>
              <Chip 
                label="Sponsor" 
                color="secondary" 
                size="small" 
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                {sponsor.email}
              </Typography>
            </Box>

            {sponsorData.organization_name && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2">
                  <strong>Organization:</strong> {sponsorData.organization_name}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Applied: {createdDate}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => openActionDialog('approve', 'sponsor', sponsor)}
            fullWidth
            sx={{ mr: 1 }}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => openActionDialog('reject', 'sponsor', sponsor)}
            fullWidth
          >
            Reject
          </Button>
        </CardActions>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1.125rem' }}>
        Pending Approvals
      </Typography>

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', mb: 1.5 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<LocalHospitalIcon />} 
            label={`Physicians (${physicians.length})`}
            iconPosition="start"
          />
          <Tab 
            icon={<VolunteerActivismIcon />} 
            label={`Sponsors (${sponsors.length})`}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {physicians.length === 0 ? (
          <Alert severity="info">
            No pending physician applications at this time.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {physicians.map((physician) => (
              <Grid item xs={12} sm={6} md={4} key={physician.id}>
                <PhysicianCard physician={physician} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {sponsors.length === 0 ? (
          <Alert severity="info">
            No pending sponsor applications at this time.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {sponsors.map((sponsor) => (
              <Grid item xs={12} sm={6} md={4} key={sponsor.id}>
                <SponsorCard sponsor={sponsor} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Approval/Rejection Dialog */}
      <Dialog 
        open={actionDialog.open} 
        onClose={closeActionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.type === 'approve' ? 'Approve Application' : 'Reject Application'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {actionDialog.type === 'approve' ? (
              <>
                Are you sure you want to approve <strong>{actionDialog.user?.first_name} {actionDialog.user?.last_name}</strong> as a {actionDialog.userType}?
                <br /><br />
                They will receive an email notification and will be able to access the platform.
              </>
            ) : (
              <>
                Are you sure you want to reject <strong>{actionDialog.user?.first_name} {actionDialog.user?.last_name}</strong>'s application?
                <br /><br />
                Please provide a reason for rejection:
              </>
            )}
          </DialogContentText>
          
          {actionDialog.type === 'reject' && (
            <TextField
              autoFocus
              margin="dense"
              label="Rejection Reason"
              fullWidth
              multiline
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please explain why this application is being rejected..."
              required
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeActionDialog} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={actionDialog.type === 'approve' ? handleApprove : handleReject}
            variant="contained"
            color={actionDialog.type === 'approve' ? 'success' : 'error'}
            disabled={processing || (actionDialog.type === 'reject' && !rejectionReason.trim())}
            startIcon={processing && <CircularProgress size={20} />}
          >
            {processing ? 'Processing...' : (actionDialog.type === 'approve' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovalsTab;
